// Comprehensive client-side file validation with XSS/EICAR/magic-byte/mime checks
import DOMPurify from "dompurify";

// ==============================
// Suspicious patterns & constants
// ==============================
const EICAR_FULL = "X5O!P%@AP[4\\PZX54(P^)7CC)7}$EICAR-STANDARD-ANTIVIRUS-TEST-FILE!$H+H*";
const eicarPattern = /X5O!P%@AP\[4\\PZX54\(P\^\)7CC\)7}\$EICAR/i;

// ==============================
// Extra suspicious PDF patterns
// ==============================
const pdfSuspiciousPatterns = [
  /\/JavaScript\s*\(/i,     // matches /JavaScript (...)
  /\/JS\s*\(/i,             // matches /JS (...)
  /\/Launch\s*\(/i,         // matches real PDF launch actions
  /\/EmbeddedFile\b/i,      // strict PDF embedded file
  /\/RichMedia\b/i          // PDF rich media object
];

/**
 * Detects script tags in HTML content using DOM parsing instead of regex
 * This addresses CodeQL security concerns about regex-based HTML filtering
 * @param {string} text - The text content to check
 * @returns {boolean} - True if script tags are detected
 */
const containsScriptTags = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }

  try {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const scriptElements = doc.querySelectorAll('script');
      if (scriptElements.length > 0) {
        return true;
      }
    }
    const sanitized = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [],
      KEEP_CONTENT: true
    });

    const lowerText = text.toLowerCase();
    if (lowerText.includes('script') && text.length > sanitized.length + 10) {
      const scriptTagStart = lowerText.indexOf('<script');
      if (scriptTagStart !== -1) {
        const afterStart = text.substring(scriptTagStart + 7);
        if (afterStart.trim().startsWith('>') || afterStart.trim().startsWith('/>') ||
          afterStart.includes('</script>') || afterStart.includes('</SCRIPT>')) {
          return true;
        }
      }
    }

    return false;
  } catch {
    return false;
  }
};

// Comprehensive XSS patterns - basic patterns (excluding script tag regex)
const xssPatterns = [
  /javascript:/i,
  /data:text\/html/i,
  /data:text\/javascript/i,
  /data:text\/svg/i,
  /on\w+\s*=/i,
  /<iframe[^>]*src\s*=\s*["'][^"']*["']/i,
  /<object[^>]*>/i,
  /<embed[^>]*>/i,
  /<form[^>]*>/i,
  /<input[^>]*>/i,
  /<meta[^>]*http-equiv/i,
  /<link[^>]*>/i,
  /<style[^>]*>[\s\S]*?<\/style>/i,
  /eval\s*\(/i,
  /Function\s*\(/i,
  /setTimeout\s*\(/i,
  /setInterval\s*\(/i,
  /innerHTML\s*=/i,
  /outerHTML\s*=/i,
  /document\.write\s*\(/i,
  /document\.writeln\s*\(/i,
  /window\.location\s*=/i,
  /document\.location\s*=/i,
  /location\.href\s*=/i,
  /location\.replace\s*\(/i,
  /location\.assign\s*\(/i,
  /<svg[^>]*on\w+\s*=/i,
  /<img[^>]*src\s*=\s*["'][^"']*["'][^>]*on\w+\s*=/i,
  /<a[^>]*href\s*=\s*["'][^"']*["'][^>]*on\w+\s*=/i,
  /<body[^>]*on\w+\s*=/i,
  /<html[^>]*on\w+\s*=/i,
  /<div[^>]*on\w+\s*=/i,
  /<span[^>]*on\w+\s*=/i,
  /<p[^>]*on\w+\s*=/i,
  /<table[^>]*on\w+\s*=/i,
  /<td[^>]*on\w+\s*=/i,
  /<tr[^>]*on\w+\s*=/i,
  /expression\s*\(/i,
  /mocha:/i,
  /livescript:/i,
  /<marquee[^>]*>/i,
  /<blink[^>]*>/i,
];

// Common XSS payloads to check for in encoded forms //
/* eslint-disable no-script-url */
const xssPayloads = [
  "<script>alert('XSS')</script>",
  "<img src=x onerror=alert(1)>",
  "<svg onload=alert(1)>",
  "javascript:alert(1)",
  "<iframe src=javascript:alert(1)>",
  "<object data=javascript:alert(1)>",
  "data:text/html,<script>alert(1)</script>",
  "<script src=data:text/javascript,alert(1)></script>",
  "<img src=data:image/svg+xml,<svg onload=alert(1)></svg>>",
  "<div onmouseover=alert(1)>Hover me</div>",
  "<body onload=alert(1)>",
  "<input onfocus=alert(1) autofocus>",
  "<a href=javascript:alert(1)>Click me</a>",
  "<form action=javascript:alert(1)><input type=submit></form>",
  "<meta http-equiv=refresh content=0;url=javascript:alert(1)>",
  "<link rel=stylesheet href=data:text/css,@import url(javascript:alert(1))>",
  "<style>@import url(javascript:alert(1))</style>",
  "<div style=background-image:url(javascript:alert(1))></div>",
  "&#60;script&#62;alert(1)&#60;/script&#62;",
  "%3Cscript%3Ealert(1)%3C/script%3E",
  "&#x3C;script&#x3E;alert(1)&#x3C;/script&#x3E;",
  "<scr<script>ipt>alert(1)</scr<script>ipt>",
  "<img src=x onerror=\"alert(1)\">",
  "<img src=x onerror='alert(1)'>",
  "<img src=x onerror=alert`1`>",
  "<script>eval(atob('YWxlcnQoMSk='))</script>",
  "data:text/html;base64,PHNjcmlwdD5hbGVydCgxKTwvc2NyaXB0Pg==",
];
/* eslint-disable no-script-url */

const blockedContentKeywords = [
  "迅捷PDF编辑器", // suspicious watermark or tool signature
  "eval(",
  "<script>",
  "javascript:",
  "onerror=",
  "onload="
];

const containsBlockedContent = (text = "") => {
  const lowerText = text.toLowerCase();
  return blockedContentKeywords.some(keyword => lowerText.includes(keyword.toLowerCase()));
};


// Scan chunk size
const SCAN_CHUNK_BYTES = 8 * 1024; // 64KB

// ==============================
// Blocked MIME types
// ==============================
const blockedMimeTypes = [
  "application/x-msdownload",
  "application/x-bat",
  "application/javascript",
  "text/javascript",
  "application/x-sh",
  "application/x-rar-compressed",
  "application/zip",
  "application/x-zip-compressed",
  "application/x-msdos-program"
];

// ==============================
// Known file magic byte signatures
// ==============================
const magicBytesSignatures = {
  pdf: ["25504446"],
  png: ["89504E47"],
  jpg: ["FFD8FF"],
  jpeg: ["FFD8FF"],
  docx: ["504B0304"],
  xlsx: ["504B0304"],
  pptx: ["504B0304"]
};

// ==============================
// Extension → expected mime map
// ==============================
const extMimeMap = {
  pdf: "pdf",
  png: "image",
  jpg: "image",
  jpeg: "image",
  docx: "officedocument.wordprocessingml",
  xlsx: "officedocument.spreadsheetml",
  pptx: "officedocument.presentationml",
  txt: "text",
  html: "html"
};

// ==============================
// Helpers
// ==============================
const readBytes = (file, length = SCAN_CHUNK_BYTES) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    const blob = file.slice(0, length);
    reader.onloadend = () => {
      try {
        resolve(new Uint8Array(reader.result || new ArrayBuffer(0)));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });

const uint8ArrayToHex = (u8) =>
  Array.from(u8).map(b => b.toString(16).padStart(2, "0")).join("").toUpperCase();

const uint8ArrayToText = (u8) => {
  try {
    return new TextDecoder("utf-8", { fatal: false }).decode(u8);
  } catch {
    return "";
  }
};

const uint8ArrayToBase64 = (u8) => {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < u8.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, i + chunkSize));
  }
  try {
    return btoa(binary);
  } catch {
    return "";
  }
};

const getMagicBytes = async (file, length = 4) => {
  const buffer = await readBytes(file, length);
  return uint8ArrayToHex(buffer);
};

const readFileChunk = async (file, length = SCAN_CHUNK_BYTES) => {
  const buffer = await readBytes(file, length);
  return {
    text: uint8ArrayToText(buffer),
    hex: uint8ArrayToHex(buffer),
    b64: uint8ArrayToBase64(buffer),
    sizeRead: buffer.length
  };
};

const hasDoubleExtension = (filename, allowedExtensions) => {
  if (!filename) return false;
  const parts = filename.toLowerCase().split(".");
  if (parts.length <= 2) return false;
  const lastExt = parts.pop();
  const secondLastExt = parts.pop();
  return allowedExtensions.includes(secondLastExt) && !allowedExtensions.includes(lastExt);
};

const hasSuspiciousFilename = (filename) => {
  if (!filename) return true;
  const lower = filename.toLowerCase();
  const suspiciousKeywords = ["xss", "script", "payload", "exploit"];
  if (filename.length > 150) return true;
  if (/[\u202E\u200B\u200E\u200F]/.test(filename)) return true;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0008\u000E-\u001F]/.test(filename)) return true;
  if (suspiciousKeywords.some(k => lower.includes(k))) return true;
  return false;
};

const isMimeMismatch = (mime, ext) => {
  if (!mime || !ext) return false;
  const expected = extMimeMap[ext];
  if (!expected) return false;
  return !mime.toLowerCase().includes(expected);
};

const getEicarBase64 = (() => { try { return btoa(EICAR_FULL); } catch { return ""; } })();

const stringToHex = (s = "") => {
  let hex = "";
  for (let i = 0; i < s.length; i++) hex += s.charCodeAt(i).toString(16).padStart(2, "0");
  return hex.toUpperCase();
};

const EICAR_HEX = stringToHex(EICAR_FULL);

const XSS_B64_PAYLOADS = (() => { try { return xssPayloads.map(p => btoa(p).toUpperCase()); } catch { return []; } })();
const XSS_HEX_PAYLOADS = (() => xssPayloads.map(p => stringToHex(p)))();
const XSS_URL_PAYLOADS = (() => { try { return xssPayloads.map(p => encodeURIComponent(p)); } catch { return []; } })();

// ==============================
// Error message mapping
// ==============================
export const getUserFriendlyErrorMessage = (errorReason, constants = {}) => {
  const errorMessages = {
    "disallowed extension": constants?.chat?.VALIDATION_DISALLOWED_EXTENSION || "File type not supported. Please upload files with allowed extensions only.",
    "blocked MIME type": constants?.chat?.VALIDATION_BLOCKED_MIME_TYPE || "File format not allowed due to security restrictions.",
    "empty file": constants?.chat?.VALIDATION_EMPTY_FILE || "Cannot upload empty files. Please select a file with content.",
    "suspicious filename": constants?.chat?.VALIDATION_SUSPICIOUS_FILENAME || "Filename contains potentially unsafe characters. Please rename the file.",
    "magic bytes mismatch": constants?.chat?.VALIDATION_MAGIC_BYTES_MISMATCH || "File content doesn't match its extension. This may indicate a corrupted or unsafe file.",
    "double extension": constants?.chat?.VALIDATION_DOUBLE_EXTENSION || "Double file extensions are not allowed for security reasons.",
    "MIME/extension mismatch": constants?.chat?.VALIDATION_MIME_EXTENSION_MISMATCH || "File type detection failed. The file may be corrupted or unsafe.",
    "EICAR signature detected": constants?.chat?.VALIDATION_EICAR_SIGNATURE_DETECTED || "Test virus signature detected. This is a harmless test file but cannot be uploaded.",
    "possible XSS payload": constants?.chat?.VALIDATION_POSSIBLE_XSS_PAYLOAD || "File contains potentially harmful content. Upload blocked for security.",
    "blocked content detected": constants?.chat?.VALIDATION_BLOCKED_CONTENT_DETECTED || "File contains blocked content and cannot be uploaded."
  };

  return errorMessages[errorReason] || constants?.chat?.VALIDATION_DEFAULT_ERROR || "File validation failed. Please try a different file.";
};

// ==============================
// Main validation function
// ==============================
export const validateFiles = async (files = [], allowedExtensions = []) => {
  const validFiles = [];
  const invalidFiles = [];
  const allowed = (allowedExtensions || []).map(e => e.toLowerCase());

  for (const file of files) {
    const filename = file?.name || "";
    const ext = filename.includes(".") ? filename.split(".").pop().toLowerCase() : "";
    const mime = (file && file.type) ? file.type.toLowerCase() : "";

    const isEmpty = file.size === 0;
    const isBlockedMime = mime && blockedMimeTypes.includes(mime);
    const isAllowedExt = ext && allowed.includes(ext);
    const suspiciousName = hasSuspiciousFilename(filename);
    const suspiciousDoubleExt = hasDoubleExtension(filename, allowed);

    // Magic bytes check
    let isValidMagic = true;
    try {
      if (ext && (ext in magicBytesSignatures)) {
        const magicHex = await getMagicBytes(file, 8);
        const allowedMagic = magicBytesSignatures[ext] || [];
        isValidMagic = allowedMagic.some(sig => magicHex.startsWith(sig));
      }
    } catch {
      isValidMagic = false;
    }

    const mimeMismatch = mime && ext && isMimeMismatch(mime, ext);

    // Content scanning (XSS/EICAR/PDF unsafe)
    let containsEicar = false;
    let containsXSS = false;
    let containsBlocked = false;
    let containsPdfUnsafe = false;

    try {
      const { text, hex, b64 } = await readFileChunk(file, SCAN_CHUNK_BYTES);
      const hexUpper = (hex || "").toUpperCase();
      const b64Upper = (b64 || "").toUpperCase();
      const textStr = text || "";

      // XSS
      if (ext !== "html") {
        // Use DOMPurify-based detection for script tags instead of regex
        containsXSS = containsScriptTags(textStr);
        if (!containsXSS) containsXSS = xssPatterns.some(pattern => pattern.test(textStr));
        if (!containsXSS) containsXSS = XSS_HEX_PAYLOADS.some(payload => hexUpper.includes(payload));
        if (!containsXSS) containsXSS = XSS_B64_PAYLOADS.some(payload => b64Upper.includes(payload));
        if (!containsXSS) containsXSS = XSS_URL_PAYLOADS.some(payload => textStr.includes(payload));
      }

      // EICAR
      containsEicar = eicarPattern.test(textStr) || /EICAR/i.test(textStr);
      if (!containsEicar && EICAR_HEX) containsEicar = hexUpper.includes(EICAR_HEX);
      if (!containsEicar && getEicarBase64) containsEicar = b64Upper.includes(getEicarBase64.toUpperCase());

      // Blocked content (keywords/watermarks)
      containsBlocked = containsBlockedContent(textStr);

      // PDF unsafe objects
      if (ext === "pdf") {
        containsPdfUnsafe = pdfSuspiciousPatterns.some(pattern => pattern.test(textStr));
      }
    } catch { }

    // Collect rejection reasons
    const rejectReasons = [];
    if (!isAllowedExt) rejectReasons.push("disallowed extension");
    if (isBlockedMime) rejectReasons.push("blocked MIME type");
    if (isEmpty) rejectReasons.push("empty file");
    if (suspiciousName) rejectReasons.push("suspicious filename");
    if (!isValidMagic && ext && (ext in magicBytesSignatures)) rejectReasons.push("magic bytes mismatch");
    if (suspiciousDoubleExt) rejectReasons.push("double extension");
    if (mimeMismatch) rejectReasons.push("MIME/extension mismatch");
    if (containsEicar) rejectReasons.push("EICAR signature detected");
    if (containsXSS) rejectReasons.push("possible XSS payload");
    if (containsBlocked) rejectReasons.push("blocked content detected");
    if (containsPdfUnsafe) rejectReasons.push("unsafe PDF actions detected");

    if (rejectReasons.length) {
      invalidFiles.push(`${filename} [${rejectReasons.join(", ")}]`);
    } else {
      validFiles.push(file);
    }
  }

  return { validFiles, invalidFiles };
};